import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, Star } from "lucide-react";
import { API, authHeaders } from "@/lib/site";

export default function StoreTab() {
  const [plans, setPlans] = useState([]);
  const [sim, setSim] = useState(false);
  const [buying, setBuying] = useState("");

  useEffect(() => {
    axios.get(`${API}/plans`).then(({ data }) => {
      setPlans(data.plans);
      setSim(data.simulation_mode);
    }).catch(() => toast.error("Could not load plans."));
  }, []);

  const buy = async (lookupKey) => {
    setBuying(lookupKey);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`,
        { lookup_key: lookupKey, origin_url: window.location.origin },
        { headers: authHeaders() });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setBuying("");
    }
  };

  return (
    <div>
      {sim && (
        <div className="mb-8 rounded-2xl border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 p-4 text-sm text-amber-800 dark:text-amber-300" data-testid="store-simulation-banner">
          Provisioning is in <strong>simulation mode</strong> — the full buy-and-deploy flow works end to end, and servers flip to real Hetzner Cloud infrastructure as soon as Wynova connects the production API.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch" data-testid="store-plans">
        {plans.map((p, i) => (
          <div key={p.lookup_key} data-testid={`store-plan-${p.lookup_key}`}
               className={`relative rounded-3xl border p-6 sm:p-8 flex flex-col ${
                 i === 1
                   ? "bg-slate-950 text-white border-primary shadow-xl shadow-blue-600/20"
                   : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
               }`}>
            {i === 1 && (
              <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary text-white text-xs font-bold uppercase tracking-wider">
                <Star className="w-3 h-3 fill-current" /> Most Popular
              </span>
            )}
            <h3 className="font-bold text-lg sm:text-xl">{p.name}</h3>
            <div className="mt-4 flex items-end gap-1">
              <span className="font-black text-4xl tracking-tight">${p.price}</span>
              <span className={`text-sm mb-1.5 ${i === 1 ? "text-slate-400" : "text-slate-500"}`}>/mo</span>
            </div>
            <ul className="mt-6 space-y-2.5 flex-1">
              {[`${p.vcpu} vCPU`, `${p.ram_gb} GB RAM`, p.disk_gb >= 1000 ? `${p.disk_gb / 1024} TB NVMe` : `${p.disk_gb} GB NVMe`,
                "Ubuntu 24.04 LTS", "Full root access", "Deployed in seconds after payment"].map((f) => (
                <li key={f} className={`flex items-center gap-2 text-sm ${i === 1 ? "text-slate-300" : "text-slate-600 dark:text-slate-300"}`}>
                  <Check className={`w-4 h-4 shrink-0 ${i === 1 ? "text-cyan-400" : "text-primary"}`} /> {f}
                </li>
              ))}
            </ul>
            <button onClick={() => buy(p.lookup_key)} disabled={!!buying} data-testid={`plan-buy-${p.lookup_key}`}
                    className={`mt-8 inline-flex justify-center items-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm transition-colors disabled:opacity-60 ${
                      i === 1 ? "bg-primary text-white hover:bg-blue-600"
                              : "border-2 border-slate-900/80 dark:border-slate-200/70 hover:bg-slate-900 hover:text-white dark:hover:bg-white dark:hover:text-slate-900"
                    }`}>
              {buying === p.lookup_key ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Deploy {p.name}
            </button>
          </div>
        ))}
      </div>
      <p className="mt-8 text-xs text-slate-400 text-center">Secure checkout by Stripe · card details never touch Wynova servers · cancel anytime</p>
    </div>
  );
}
